import React, {Dispatch, useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {useParams, useHistory, Link} from 'react-router-dom'
import Sticky from 'react-stickynode';
import ReactSelect from 'react-select/async';
import Select from 'react-select';

import {ChangeStepOptions, Choice, StatusMessage} from '../types';

import {Form} from '../components/Form';
import {LoadingIndicator} from '../components/LoadingIndicator';
import {EntryComments} from '../components/EntryComments';
import {EntryTimeline} from '../components/EntryTimeline';
import {EntryWorkflowStatus} from '../components/EntryWorkflowStatus';
import {EntryAdminActions} from '../components/EntryAdminActions';
import {EntryMetrics} from '../components/EntryMetrics';
import {Modal} from '../components/Modal';
import {FormContainer} from '../components/FormContainer';
import {ActivityButton} from '../components/ActivityButton';

import {formIsBusy} from '../_helpers/form';
import {postStatusMessageWithTimeout} from '../_actions/statusMessage';
import {User} from '../_services/User';
import {AdminActionCommentModal} from '../components/AdminActionCommentModal';
import {WorkflowDiagram} from '../components/WorkflowDiagram';
import {useEntry} from '../_hooks/useEntry'
import {handleEmailLookup} from '../_helpers/select';

type EntryProps = {
    dispatchStatusMessage: (message: StatusMessage) => void;
};

type AdminActionCommentModalState = {
    show: boolean;
    action?: Choice;
};
const initialAdminActionModalState = {
    show: false,
    action: undefined
};

type ChangeFinalStatusModalState = {
    show: boolean;
    finalStatus: string;
}

const initialChangeFinalStatusModalState: ChangeFinalStatusModalState = {
    show: false,
    finalStatus: ''
}

type UserChangeStepConfirmationModalState = {
    show: boolean,
    changeStepOptions?: ChangeStepOptions
}

const initialUserChangeStepConfirmationModalState: UserChangeStepConfirmationModalState = {
    show: false,
}

const Entry: React.FunctionComponent<EntryProps> = ({
                                                        dispatchStatusMessage
                                                    }): JSX.Element => {

    const {entryId} = useParams();
    const history = useHistory();
    const {
        entry,
        formState,
        setFormState,
        refreshEntry,
        entryIsLoading,
        entryLoadErrorMessage,
        isDirty,
        statusIsUpdating,
        statusUpdateErrorMessage,
        entryNote,
        setEntryNote,
        entryNoteErrorMessage,
        clearEntryNoteError,
        processStatusUpdate,
        doAdminAction,
        doUserChangeStep,
        userChangeStepErrorMessage
    } = useEntry(entryId, dispatchStatusMessage)

    const [showAdminAddAssigneeModal, setShowAdminAddAssigneeModal] = useState<boolean>(false);
    const [adminActionModalState, setAdminActionModalState] = useState<AdminActionCommentModalState>(initialAdminActionModalState);
    const [userChangeStepConfirmationModalState, setUserChangeStepConfirmationModalState] = useState<UserChangeStepConfirmationModalState>({...initialUserChangeStepConfirmationModalState});
    const [showTransferModal, setShowTransferModal] = useState<boolean>(false);
    const [printAllDetails, setPrintAllDetails] = useState<boolean>(User.getInstance().getPrintAllDetails());
    const [selectedLocale, setSelectedLocale] = useState<any>({label: 'English', value: 'en-US'});
    const [showEntryNoteModal, setShowEntryNoteModal] = useState<boolean>(false);
    const [changeFinalStatusModal, setChangeFinalStatusModal] = useState<any>({...initialChangeFinalStatusModalState});

    useEffect(() => {
        window.addEventListener('beforeunload', warnBeforeLeave)
        return () => {
            window.removeEventListener('beforeunload', warnBeforeLeave)
        }
    }, [])

    const warnBeforeLeave = (e: any) => {
        if (isDirty) {
            e.preventDefault();
            e.returnValue = '';
        } else {
            delete e['returnValue'];
        }
    }

    const handleAdminAction = (actionChoice: Choice, data: any = null): void => {
        const action = actionChoice.value;
        if (action === 'add_assignees' && data === null) {
            setShowAdminAddAssigneeModal(true);
            return;
        } else if (data === null) {
            setAdminActionModalState({
                ...adminActionModalState,
                show: true,
                action: actionChoice
            });
            return;
        }

        doAdminAction(action, data);
    };

    const handleUserChangeStep = (userChangeStepOptions: ChangeStepOptions, fromModal: boolean = false): void => {

        // if there is confirmation text and this call isnt coming from the confirmation modal,
        // show the modal and bail
        if (userChangeStepOptions.confirmationText && !fromModal) {
            setUserChangeStepConfirmationModalState({
                show: true,
                changeStepOptions: userChangeStepOptions
            });
            return;
        }

        doUserChangeStep(userChangeStepOptions.stepId);
    };

    const disableActivityButtons = formIsBusy(formState);

    // @ts-ignore
    return (
        <>
            {entry && entry.form &&
                <div className="row no-gutters" style={{margin: '0 -15px'}}>
                    <div className="col-12 text-right">
                        <WorkflowDiagram workflowId={entry.form.id} entryId={entryId}/>
                    </div>
                </div>
            }
            <div className="row">
                <div className="col-12">
                    {entryLoadErrorMessage && <p className="alert alert-danger">{entryLoadErrorMessage}</p>}
                    {entryIsLoading && <LoadingIndicator/>}

                    <div className="d-flex justify-content-between">
                        <div>
                            {entry.id && <p><a href="#"
                                               className="back-link"
                                               onClick={(e) => {
                                                   e.preventDefault();
                                                   history.goBack()
                                               }}>Back</a></p>}
                        </div>
                        {entry?.form?.locales?.length > 0 &&
                            <div style={{width: '300px'}}>
                                <Select
                                    value={selectedLocale}
                                    onChange={(option: any) => {
                                        setSelectedLocale(option);
                                    }}
                                    options={[
                                        {label: 'English', value: 'en-US'},
                                        {label: 'Russian', value: 'ru-RU'},
                                    ]}
                                />
                            </div>
                        }
                    </div>

                    {entry.id && <h1 className={'abb-header'}>{entry.form.title}</h1>}
                    {entry?.step?.status && <h2 className="step-name"><span>Step - </span>{entry.step.name}</h2>}
                    {entry?.id && <h2 className="entry-id"><span>Entry ID - </span>{entry.id}</h2>}

                    {entry.id &&
                        <div className={'row' + (printAllDetails ? ' print-all-details' : '')}>
                            <div id="workflow-form-container-col" className="col-12 col-sm-8 col-xl-9 order-2 order-sm-1">
                                {entry.step.instructions && entry.step.userCanUpdate &&
                                    <div className="entry-instructions alert alert-warning"
                                         dangerouslySetInnerHTML={{__html: entry.step.instructions}}
                                    />}
                                <FormContainer>
                                    <Form form={entry.form}
                                          displayOnly={true}
                                          editableFieldIds={entry.step.editableFields}
                                          highlightEditableFields={entry.step.highlightEditableFields}
                                          displayFields={entry.step.displayFieldsSelected}
                                          displayFieldsMode={entry.step.displayFieldsMode}
                                          formState={formState}
                                          setFormState={setFormState}
                                          showAdministrativeFields={true}
                                          showPrivate={entry.step?.canViewPrivate || false}
                                          step={entry.step}
                                          locale={selectedLocale?.value || 'en-US'}
                                    />
                                </FormContainer>

                                <EntryTimeline timeline={entry.timeline}/>
                            </div>
                            <div id="workflow-status-container-col" className="col-12 col-sm-4 col-xl-3 order-1 order-sm-2">
                                <Sticky enabled={true}
                                        top={70}
                                >
                                    <div className="form-check text-right">
                                        <label className="form-check-label"
                                               htmlFor="include-all-details-print"
                                               style={{marginRight: '1.5rem'}}
                                        >Include all details in Print?</label>
                                        <input className="form-check-input"
                                               type="checkbox"
                                               id="include-all-details-print"
                                               checked={printAllDetails}
                                               onChange={(e) => {
                                                   setPrintAllDetails(e.target.checked);
                                                   User.getInstance().setPrintAllDetails(e.target.checked)
                                               }}
                                        />
                                    </div>

                                    <EntryWorkflowStatus
                                        entry={entry}
                                        statusUpdateErrorMessage={statusUpdateErrorMessage}
                                        entryNote={entryNote}
                                        setEntryNote={setEntryNote}
                                        entryNoteErrorMessage={entryNoteErrorMessage}
                                        setEntryNoteErrorMessage={() => clearEntryNoteError()}
                                        statusIsUpdating={statusIsUpdating}
                                        processStatusUpdate={processStatusUpdate}
                                        disableActivityButtons={disableActivityButtons}
                                        setShowTransferModal={setShowTransferModal}
                                        setShowEntryNoteModal={setShowEntryNoteModal}
                                        showChangeFinalStatusModal={(show: boolean, finalStatus: string) => {
                                            setChangeFinalStatusModal((state: ChangeFinalStatusModalState) => ({
                                                ...state,
                                                show,
                                                finalStatus
                                            }))
                                        }}
                                    />

                                    {entry.step.flowAdminActions && entry.step.flowAdminActions.length > 0 &&
                                        <EntryAdminActions
                                            isActive={statusIsUpdating === 'admin'}
                                            options={entry.step.flowAdminActions}
                                            onSelect={(_value, choice) => {
                                                if (choice) {
                                                    handleAdminAction(choice)
                                                } else {
                                                    throw Error('Invalid admin action selected');
                                                }
                                            }}
                                        />
                                    }

                                    {/*User.getInstance().hasRole('it') &&
                                <ItAdminActions
                                    entry={entry}
                                />
                                */}

                                    {/* if user is an assignee, then comments are not available to them */}
                                    {entry.form.flow_form_allow_comments && entry.workflow.status === 'Pending' &&
                                        <EntryComments
                                            entryId={entry.id}
                                            onSave={refreshEntry}
                                        />
                                    }

                                    {entry.step.userCanChangeStep &&
                                        <>
                                        {
                                            (Array.isArray(entry.step.userChangeStepOptions)) ?
                                                <div className={'d-flex justify-content-center m-0 p-0'} style={{gap: '4px'}}>
                                                    {entry.step.userChangeStepOptions.map((userChangeStepOption: ChangeStepOptions) => (
                                                        <ActivityButton type="button"
                                                                        className={'btn ' + userChangeStepOption.buttonType}
                                                                        isActive={statusIsUpdating === 'userChangeStep'}
                                                                        disabled={statusIsUpdating === 'userChangeStep'}
                                                                        onClick={() => handleUserChangeStep(userChangeStepOption)}
                                                                        dangerouslySetInnerHTML={{__html: userChangeStepOption.buttonText}}
                                                                        style={{minWidth: '33%', maxWidth: '50%'}}
                                                        />
                                                    ))}
                                                </div>
                                                :
                                                <ActivityButton type="button"
                                                                className={'btn btn-block ' + entry.step.userChangeStepOptions.buttonType}
                                                                isActive={statusIsUpdating === 'userChangeStep'}
                                                                disabled={statusIsUpdating === 'userChangeStep'}
                                                                onClick={() => handleUserChangeStep(entry.step.userChangeStepOptions)}
                                                                dangerouslySetInnerHTML={{__html: entry.step.userChangeStepOptions.buttonText}}
                                                />
                                        }
                                        </>
                                    }
                                    {userChangeStepErrorMessage && <p className="pt-2 text-right text-danger">{userChangeStepErrorMessage}</p>}

                                    {entry.form?.flow_can_copy &&
                                        <Link
                                            to={`/new/${entry.form.id}?__copyFrom=${entry.id}`}
                                            className="btn btn-block btn-secondary">
                                            Copy to New Workflow
                                        </Link>
                                    }

                                    <div className={'mt-2'}>
                                        <EntryMetrics entry={entry}/>
                                    </div>

                                </Sticky>
                            </div>
                        </div>
                    }

                    {showTransferModal &&
                        <TransferModal
                            close={() => setShowTransferModal(false)}
                            save={(transferAssignees: any, transferAssigneesReplaceCurrent: any) => {
                                processStatusUpdate('transfer', false, {
                                    __transferToAssignees: transferAssignees,
                                    __transferToReplaceCurrent: transferAssigneesReplaceCurrent
                                })();
                            }}
                        />
                    }

                    {showAdminAddAssigneeModal &&
                        <AddAssigneeModal
                            close={() => setShowAdminAddAssigneeModal(false)}
                            addAssignees={(assignees: any, comments: any) => {
                                handleAdminAction({value: 'add_assignees', text: 'Add Assignee(s)', label: 'Add Assignee(s)'}, {
                                    assignees,
                                    comments
                                });
                            }}
                            userIsSupport={User.getInstance().hasRole('is_support')}
                        />
                    }

                    <AdminActionCommentModal
                        show={adminActionModalState.show}
                        action={adminActionModalState.action}
                        onCloseCancel={() => {
                            setAdminActionModalState({
                                ...initialAdminActionModalState
                            });
                        }}
                        onCloseOk={(action, comments) => {
                            handleAdminAction(action, {comments});
                            setAdminActionModalState({
                                ...initialAdminActionModalState
                            });
                        }}
                        userIsSupport={User.getInstance().hasRole('is_support')}
                    />

                    {userChangeStepConfirmationModalState?.show && userChangeStepConfirmationModalState.changeStepOptions &&
                        <UserChangeStepConfirmationModal
                            close={() => setUserChangeStepConfirmationModalState({...initialUserChangeStepConfirmationModalState})}
                            text={userChangeStepConfirmationModalState.changeStepOptions.confirmationText || 'Ok'}
                            changeStep={() => handleUserChangeStep(userChangeStepConfirmationModalState.changeStepOptions, true)}
                        />
                    }

                    {showEntryNoteModal &&
                        <EntryNoteModal
                            close={() => setShowEntryNoteModal(false)}
                            value={entryNote}
                            onChange={(value: string) => {
                                entryNoteErrorMessage && clearEntryNoteError();
                                setEntryNote(value);
                            }}
                        />
                    }

                    {changeFinalStatusModal.show &&
                        <ChangeFinalStatusModal
                            close={() => setChangeFinalStatusModal({...initialChangeFinalStatusModalState})}
                            save={(note: string) => {
                                processStatusUpdate('change_final_status', false, {
                                    changeFinalStatus: changeFinalStatusModal.finalStatus,
                                    __gravityflow_note: note
                                })();
                            }}
                        />
                    }
                </div>
            </div>
        </>
    );
};

const AddAssigneeModal = ({
                              addAssignees,
                              close,
                              userIsSupport = false,
                          }) => {

    const [adminAdditionalAssignees, setAdminAdditionalAssignees] = useState<Choice[]>([]);
    const [adminAddAssigneeComment, setAdminAddAssigneeComment] = useState<string>('');
    const [snowInc, setSnowInc] = useState<string>('');
    const [validationErrors, setValidationErrors] = useState<any>({});

    return (
        <Modal>
            <div className="modal-header">
                <h5 className="modal-title">Add Assignee(s) to Current Step</h5>
                <button type="button"
                        className="close"
                        tabIndex={-1}
                        onClick={() => {
                            close();
                            setAdminAddAssigneeComment('');
                        }}
                >×
                </button>
            </div>
            <div className="modal-body">
                <p>Search for assignees by email. Add one or more assignees then click
                    <em>Add Assignee(s)</em> to add to the current step.</p>

                <div className="form-group">
                    <ReactSelect
                        isMulti={true}
                        value={adminAdditionalAssignees}
                        onChange={(option: any) => {
                            setAdminAdditionalAssignees(option);
                        }}
                        placeholder="Enter an email address"
                        loadOptions={handleEmailLookup}
                    />
                </div>
                {userIsSupport &&
                <div className="form-group">
                    <p>Provide SNOW INC# for this Action</p>
                    <input
                        type="text"
                        className="form-control"
                        value={snowInc}
                        onChange={(e) => {
                            setSnowInc(e.target.value);
                        }}
                    />
                    {validationErrors?.snowInc && <p className={'text-danger'}>A SNOW Incident# is required. Should begin with <em>INC</em></p>}
                </div>
                }
                <div className="form-group">
                    <p>Enter any notes / comments you want to attach to this admin activity</p>
                    <textarea
                        className="form-control"
                        value={adminAddAssigneeComment}
                        onChange={(e) => {
                            setAdminAddAssigneeComment(e.target.value);
                        }}
                    />
                    {validationErrors?.comment && <p className={'text-danger'}>A comment required</p>}
                </div>
            </div>
            <div className="modal-footer">
                <button type="button"
                        className="btn btn-primary"
                        onClick={() => {
                            setValidationErrors({});

                            if (userIsSupport) {
                                let hasValidationErrors = false;
                                if (!adminAddAssigneeComment) {
                                    setValidationErrors(state => ({
                                        ...state,
                                        comment: true
                                    }));
                                    hasValidationErrors = true;
                                }
                                if (!snowInc || !snowInc.startsWith('INC')) {
                                    setValidationErrors(state => ({
                                        ...state,
                                        snowInc: true
                                    }));
                                    hasValidationErrors = true;
                                }

                                if (hasValidationErrors) {
                                    return;
                                }
                            }

                            addAssignees(adminAdditionalAssignees, adminAddAssigneeComment);
                            setAdminAdditionalAssignees([]);
                            setAdminAddAssigneeComment('');
                            setSnowInc('');
                            setValidationErrors({});
                            close();
                        }}
                >Add Assignee(s)
                </button>
            </div>

        </Modal>
    )
}

const UserChangeStepConfirmationModal = ({
                                             close,
                                             changeStep,
                                             text
                                         }) => {
    return (
        <>
            <Modal>
                <div className="modal-body"
                     style={{padding: '0px'}}
                     dangerouslySetInnerHTML={{__html: text}}
                />
                <div className="modal-footer"
                     style={{padding: '.5rem 1rem'}}
                >
                    <button type="button"
                            className="btn btn-sm btn-light"
                            onClick={close}
                    >Cancel
                    </button>
                    <button type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() => {
                                changeStep();
                                close();
                            }}
                    >Continue
                    </button>
                </div>
            </Modal>
        </>
    )
}

const EntryNoteModal = ({
                            close,
                            value,
                            onChange
                        }) => {
    return (
        <Modal size={'lg'}>
            <div className="modal-header">
                <h5 className="modal-title">Comments</h5>
                <button type="button"
                        className="close"
                        tabIndex={-1}
                        onClick={close}
                >×
                </button>
            </div>
            <div className="modal-body" style={{padding: '0.5rem'}}>
                            <textarea id="gravityflow-note"
                                      autoFocus={true}
                                      rows={25}
                                      style={{width: '100%', height: '100%'}}
                                      className="form-control"
                                      value={value}
                                      onChange={(e) => onChange(e.target.value)}
                                      onKeyUp={(e) => {
                                          if (e.key === 'Escape') {
                                              close();
                                          }
                                      }}
                            />
            </div>
            <div className="modal-footer">
                <button type="button"
                        className="btn btn-secondary"
                        onClick={close}
                >Done
                </button>
            </div>
        </Modal>
    )
}

const ChangeFinalStatusModal = ({
                                    close,
                                    save
                                }) => {
    const [note, setNote] = useState('');
    return (
        <Modal>
            <div className="modal-header">
                <h5 className="modal-title">Change Workflow Status</h5>
                <button type="button"
                        className="close"
                        tabIndex={-1}
                        onClick={close}
                >×
                </button>
            </div>
            <div className="modal-body" style={{padding: '0.5rem'}}>
                <p>Enter any notes / comments you want to attach to this activity.</p>
                <textarea id="gravityflow-note"
                          autoFocus={true}
                          className="form-control"
                          value={note}
                          onChange={(e) => {
                              const v = e.target.value;
                              setNote(v);
                          }}
                />
            </div>
            <div className="modal-footer">
                <button type="button"
                        className="btn btn-secondary"
                        onClick={close}
                >Cancel
                </button>
                <button type="button"
                        className="btn btn-primary"
                        onClick={() => {
                            save(note);
                            close();
                        }}
                >Continue
                </button>
            </div>
        </Modal>
    )
}

const TransferModal = ({
    close,
    save
}) => {
    const [transferAssignees, setTransferAssignees] = useState<Choice[]>([])
    const [transferAssigneesReplaceCurrent, setTransferAssigneesReplaceCurrent] = useState<boolean>(false);
    return (
        <Modal>
            <div className="modal-header">
                <h5 className="modal-title">Transfer Ownership to Another Assignee</h5>
                <button type="button"
                        className="close"
                        tabIndex={-1}
                        onClick={close}
                >×
                </button>
            </div>
            <div className="modal-body">
                <p>Search for assignees by email. Add one or more assignees then click
                    <em>Transfer to Assignee(s)</em> to transfer the workflow.</p>

                <div className="form-group">
                    <ReactSelect
                        isMulti={true}
                        value={transferAssignees}
                        onChange={(option: any) => {
                            setTransferAssignees(option);
                        }}
                        placeholder="Enter an email address"
                        loadOptions={handleEmailLookup}
                    />
                </div>
                <div className="form-group">
                    <div className="form-check">
                        <input className="form-check-input"
                               type="checkbox"
                               checked={transferAssigneesReplaceCurrent}
                               onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                   setTransferAssigneesReplaceCurrent(e.target.checked);
                               }}
                               id="transferReplaceCurrent"
                        />
                        <label className="form-check-label" htmlFor="transferReplaceCurrent">
                            Selected Assignee(s) should replace me
                        </label>
                    </div>
                </div>
            </div>
            <div className="modal-footer">
                <button type="button"
                        className="btn btn-primary"
                        onClick={() => {
                            save(transferAssignees, transferAssigneesReplaceCurrent);
                            close();
                        }}
                >Transfer to Assignee(s)
                </button>
            </div>

        </Modal>
    )
}

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
    return {
        dispatchStatusMessage: (message: StatusMessage) => {
            dispatch(postStatusMessageWithTimeout(message, 5000));
        }
    };
};

const ConnectedEntry = connect(null, mapDispatchToProps)(Entry);
export {ConnectedEntry as Entry};
